import React from 'react';
import { getDisplayInvoiceAmount } from '../../utils/invoiceCardUtils';
import { DashboardPageHeader, dashboardBtnPrimary } from './DashboardChrome';

const OverviewSection = ({ quotes, clients, projects, appointments, teams, dayAssignments, editQuote, navigateToSection }) => {
  const lastThreeQuotes = quotes.slice(0, 3);

  const getOngoingProjects = () => {
    const today = new Date();
    return projects.filter(project => {
      if (!project.startDate || !project.deadline) return false;
      const startDate = new Date(project.startDate);
      const deadline = new Date(project.deadline);
      return startDate <= today && deadline >= today;
    }).slice(0, 3);
  };

  const calculateProgress = (project) => {
    if (!project.startDate || !project.deadline) return 0;
    const startDate = new Date(project.startDate);
    const deadline = new Date(project.deadline);
    const today = new Date();
    const totalDuration = deadline - startDate;
    const elapsed = today - startDate;
    if (totalDuration <= 0) return 100;
    if (elapsed <= 0) return 0;
    if (elapsed >= totalDuration) return 100;
    return Math.round((elapsed / totalDuration) * 100);
  };

  /** Sum saved invoice amounts only (same logic as invoice list rows), not quote-form estimates. */
  const totalRevenue = quotes.reduce((sum, q) => {
    const amt = getDisplayInvoiceAmount(q);
    return sum + (typeof amt === 'number' && !Number.isNaN(amt) ? amt : 0);
  }, 0);

  const activeProjectsCount = projects.filter(p => {
    if (!p.startDate || !p.deadline) return false;
    const today = new Date();
    return new Date(p.startDate) <= today && new Date(p.deadline) >= today;
  }).length;

  const stats = [
    {
      label: 'Total clients',
      value: clients.length,
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      color: 'bg-blue-50 text-blue-600',
      section: 'clients',
    },
    {
      label: 'Total invoices',
      value: quotes.length,
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: 'bg-accent/15 text-accent-dark',
      section: 'invoices',
    },
    {
      label: 'Active projects',
      value: activeProjectsCount,
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      color: 'bg-primary/10 text-primary',
      section: 'projects',
    },
    {
      label: 'Total revenue',
      value: `$${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'bg-emerald-50 text-emerald-600',
      section: 'invoices',
    },
  ];

  const ongoingProjects = getOngoingProjects();

  return (
    <div className="mx-auto max-w-7xl space-y-8 pb-10">
      <DashboardPageHeader
        eyebrow="Dashboard"
        title="Overview"
        description="Your business at a glance — recent invoices, active jobs, and key numbers all in one place."
      />

      {/* Top row — stats + invoices */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

        {/* Business at a glance */}
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
          <div className="mb-5 flex items-center space-x-3">
            <div className="rounded-xl bg-primary/10 p-2 text-primary">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900">Business at a glance</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {stats.map((stat) => (
              <button
                key={stat.label}
                type="button"
                onClick={() => navigateToSection(stat.section)}
                className="group rounded-2xl border border-slate-100 bg-slate-50/50 p-4 text-left transition-all hover:border-slate-200 hover:bg-white hover:shadow-sm"
              >
                <div className={`mb-3 inline-flex rounded-xl p-2 ${stat.color}`}>
                  {stat.icon}
                </div>
                <p className="text-2xl font-bold text-slate-900 leading-none mb-1">{stat.value}</p>
                <p className="text-xs text-slate-500 font-medium">{stat.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Recent invoices */}
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-xl bg-accent/15 p-2 text-accent-dark">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900">Recent invoices</h3>
            </div>
            <button type="button" onClick={() => navigateToSection('invoices')} className={`${dashboardBtnPrimary} px-4 py-2 text-sm`}>
              View all
            </button>
          </div>

          <div className="space-y-3">
            {lastThreeQuotes.length > 0 ? (
              lastThreeQuotes.map((quote) => {
                const client = clients.find(c => c.id === quote.clientId);
                const invAmt = getDisplayInvoiceAmount(quote);
                const displayTotal =
                  typeof invAmt === 'number' && !Number.isNaN(invAmt)
                    ? invAmt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : null;
                return (
                  <div
                    key={quote.id}
                    className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50/50 p-4 transition-all hover:border-slate-200 hover:bg-white hover:shadow-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {quote.quoteName || quote.projectName || 'Invoice'}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>{client?.name || 'Client'}</span>
                        <span>•</span>
                        <span>{quote.createdAt ? (quote.createdAt.toDate ? quote.createdAt.toDate().toLocaleDateString() : new Date(quote.createdAt).toLocaleDateString()) : 'N/A'}</span>
                        {displayTotal ? (
                          <>
                            <span>•</span>
                            <span className="font-semibold text-slate-600">${displayTotal}</span>
                          </>
                        ) : null}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => navigateToSection('invoices', quote.id)}
                        className="rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-primary-dark"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500 font-medium">No invoices yet</p>
                <p className="text-xs text-gray-400 mt-1">Create your first invoice from the Invoices tab.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ongoing projects */}
      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="rounded-xl bg-primary/10 p-2 text-primary">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900">Ongoing projects</h3>
          </div>
          <button type="button" onClick={() => navigateToSection('projects')} className={`${dashboardBtnPrimary} px-4 py-2 text-sm`}>
            View all
          </button>
        </div>

        <div className="space-y-4">
          {ongoingProjects.length > 0 ? (
            ongoingProjects.map((project) => {
              const progress = calculateProgress(project);
              const daysLeft = project.deadline ? Math.ceil((new Date(project.deadline) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

              const getProgressColor = (pct) => {
                if (pct >= 80) return 'from-emerald-500 to-emerald-600';
                if (pct >= 60) return 'from-amber-400 to-amber-500';
                if (pct >= 40) return 'from-primary to-primary-light';
                return 'from-red-400 to-red-500';
              };

              return (
                <div
                  key={project.id}
                  className="rounded-2xl border border-slate-100 bg-slate-50/50 p-5 transition-all hover:border-slate-200 hover:bg-white hover:shadow-sm"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-gray-900">{project.name}</h4>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      daysLeft <= 7 ? 'bg-red-100 text-red-700' :
                      daysLeft <= 14 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {daysLeft} days left
                    </span>
                  </div>

                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-2">
                      <span className="font-medium">Progress</span>
                      <span className="font-bold">{progress}%</span>
                    </div>
                    <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
                      <div
                        className={`bg-gradient-to-r ${getProgressColor(progress)} h-3 rounded-full transition-all duration-500 ease-out`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between text-xs text-gray-500">
                    <span className="flex items-center space-x-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Start: {project.startDate ? new Date(project.startDate).toLocaleDateString() : 'N/A'}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Due: {project.deadline ? new Date(project.deadline).toLocaleDateString() : 'N/A'}</span>
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <p className="text-sm text-gray-500 font-medium">No ongoing projects</p>
              <p className="text-xs text-gray-400 mt-1">All caught up on your projects!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverviewSection;
